/**
 * IdleMount — defers mounting children until the browser is idle,
 * or after a fallback timeout. Used to keep heavy SEO injectors
 * (LongTailKeywordInjector, GlobalSearchEngineOptimizer,
 * AdvancedCrawlDirectives, YandexBaiduOptimizer, SpeculationRules,
 * CrawlabilityEnhancer) out of the initial render path so they
 * don't bloat FCP/LCP or main-thread work.
 *
 * Crawlers still see the rendered output because:
 *  - SSR/Cloudflare worker emits a static HTML snapshot.
 *  - Hydration eventually mounts them on idle.
 */
import { ReactNode, useEffect, useState } from 'react';

interface IdleMountProps {
  children: ReactNode;
  /** Fallback delay (ms) when requestIdleCallback is unavailable. */
  timeout?: number;
}

export function IdleMount({ children, timeout = 2000 }: IdleMountProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(() => setReady(true), { timeout });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(() => setReady(true), timeout);
    return () => window.clearTimeout(t);
  }, [timeout]);

  if (!ready) return null;
  return <>{children}</>;
}

export default IdleMount;
